import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useTheme,
    CircularProgress,
    Avatar,
    Button
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useLoginAnalyticsDetails } from "../features/users/useUsers";
import { useState } from "react";

export default function LoginAnalyticsDetails({ open, onClose, filters }) {
    const theme = useTheme();
    const [page, setPage] = useState(0);
    const rowsPerPage = 1000; // Increased from 100 to fetch more records

    const { data, isLoading } = useLoginAnalyticsDetails({ ...filters, page, limit: rowsPerPage }, { enabled: open });

    const sessions = data?.items || [];

    const getTitle = () => {
        if (!filters.type) return "Details";
        const typeLabel = filters.type.replace("login_", "").replace("_", " ");
        const valueLabel = filters.type === 'month'
            ? ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][Number(filters.value)]
            : filters.value;

        return `${typeLabel.charAt(0).toUpperCase() + typeLabel.slice(1)} Details: ${valueLabel || ''} (${filters.year || ''})`;
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    minHeight: "60vh",
                    bgcolor: theme.palette.background.default
                }
            }}
        >
            <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" component="div">
                    {getTitle()}
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : sessions.length === 0 ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography>No records found for this selection.</Typography>
                    </Box>
                ) : (
                    <TableContainer component={Box} elevation={0} variant="outlined">
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>User</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell>App</TableCell>
                                    <TableCell>Method</TableCell>
                                    <TableCell>Location</TableCell>
                                    <TableCell>IP</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sessions.map((session) => (
                                    <TableRow key={session._id} hover>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Avatar
                                                    src={session.userId?.profilePicture}
                                                    alt={session.userId?.firstName}
                                                    sx={{ width: 32, height: 32 }}
                                                >
                                                    {session.userId?.email?.charAt(0).toUpperCase()}
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="body2" fontWeight="bold">
                                                        {session.userId?.firstName} {session.userId?.lastName}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {session.userId?.email}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            {new Date(session.createdAt).toLocaleString()}
                                        </TableCell>
                                        <TableCell>{session.login_app}</TableCell>
                                        <TableCell align="center">
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {/* You could add icons here based on method */}
                                                {session.login_method}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            {session.IpLookup ? `${session.IpLookup.city || ''}, ${session.IpLookup.country || ''}` : 'Unknown'}
                                        </TableCell>
                                        <TableCell>{session.login_ip || "Not Provided"}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>
        </Dialog>
    );
}
